import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { prefetchOpsBundle, prefetchRoute } from '@/lib/route-prefetch.ts';
import {
  isSpatialDockOverflowPath,
  spatialDockOverflow,
  spatialDockPrimary,
  type SpatialNavItem,
  type SpatialNavSection,
} from '@/lib/spatial-nav.ts';
import { useUIStore } from '@/stores/ui-store.ts';
import { SpatialLayoutSwitch } from './SpatialLayoutSwitch.tsx';
import { cn } from '@/lib/utils.ts';

const ARROW_KEYS = new Set(['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

function iconNodes(root: ParentNode) {
  return [...root.querySelectorAll<HTMLElement>('[data-spatial-icon]')];
}

function focusIcon(nav: HTMLElement, index: number) {
  const nodes = iconNodes(nav);
  const next = Math.max(0, Math.min(nodes.length - 1, index));
  nodes[next]?.focus();
}

function DesktopIcon({
  item,
  index,
  t,
}: {
  item: SpatialNavItem;
  index: number;
  t: (key: string) => string;
}) {
  const location = useLocation();
  const Icon = item.icon;
  const label = t(item.labelKey);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLAnchorElement>) => {
    if (!ARROW_KEYS.has(event.key)) return;
    const nav = event.currentTarget.closest('nav');
    if (!(nav instanceof HTMLElement)) return;
    const nodes = iconNodes(nav);
    const count = nodes.length;
    const current = Number(event.currentTarget.dataset.spatialIcon);
    event.preventDefault();
    if (event.key === 'Home') {
      focusIcon(nav, 0);
      return;
    }
    if (event.key === 'End') {
      focusIcon(nav, count - 1);
      return;
    }
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    focusIcon(nav, (current + delta + count) % count);
  };

  return (
    <NavLink
      to={item.to}
      end={item.end ?? item.to === '/'}
      aria-label={label}
      data-spatial-icon={index}
      onPointerEnter={() => prefetchRoute(item.to)}
      onKeyDown={onKeyDown}
      className={({ isActive }) => {
        const active = item.isActive ? item.isActive(location.pathname) : isActive;
        return cn('spatial-desk-icon', active && 'is-active');
      }}
    >
      <span className="spatial-desk-icon-tile" aria-hidden="true">
        <Icon className="size-[25px]" strokeWidth={1.5} />
      </span>
      <span className="spatial-desk-icon-label">{label}</span>
    </NavLink>
  );
}

export function SpatialDesktopIcons() {
  const { t } = useTranslation();
  const location = useLocation();
  const layout = useUIStore((state) => state.spatialLayout);
  const setLayout = useUIStore((state) => state.setSpatialLayout);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const overflow = spatialDockOverflow();
  const overflowActive = isSpatialDockOverflowPath(location.pathname);

  useEffect(() => {
    if (!location.pathname.startsWith('/servers')) return;
    const ric =
      window.requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 200) as unknown as number);
    const id = ric(() => prefetchOpsBundle());
    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(id as number);
      } else {
        clearTimeout(id as number);
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onPointer = (event: PointerEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const onMoreKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!ARROW_KEYS.has(event.key)) return;
    const nav = event.currentTarget.closest('nav');
    if (!(nav instanceof HTMLElement)) return;
    const nodes = iconNodes(nav);
    const count = nodes.length;
    const current = Number(event.currentTarget.dataset.spatialIcon);
    event.preventDefault();
    if (event.key === 'Home') {
      focusIcon(nav, 0);
      return;
    }
    if (event.key === 'End') {
      focusIcon(nav, count - 1);
      return;
    }
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    focusIcon(nav, (current + delta + count) % count);
  };

  return (
    <nav className="spatial-icon-field" data-edge="bottom" aria-label={t('sidebar.desktopIcons')}>
      <div className="spatial-icon-field-view">
        <SpatialLayoutSwitch layout={layout} onLayoutChange={setLayout} />
      </div>
      <div className="spatial-dock-tray">
        <div className="spatial-icon-field-main">
          <div className="spatial-desk-group" role="group" aria-label={t('sidebar.sectionOps')}>
            <div className="spatial-desk-group-row">
              {spatialDockPrimary.map((item, index) => (
                <DesktopIcon key={item.to} item={item} index={index} t={t} />
              ))}
            </div>
          </div>
        </div>
        <div className="spatial-icon-field-pin" ref={moreRef}>
          <div className="spatial-desk-more">
            <button
              type="button"
              data-spatial-icon={spatialDockPrimary.length}
              className={cn('spatial-desk-icon', (moreOpen || overflowActive) && 'is-active')}
              aria-label={t('sidebar.more')}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              onClick={() => setMoreOpen((open) => !open)}
              onKeyDown={onMoreKeyDown}
            >
              <span className="spatial-desk-icon-tile" aria-hidden="true">
                <MoreHorizontal className="size-[25px]" strokeWidth={1.5} />
              </span>
              <span className="spatial-desk-icon-label">{t('sidebar.more')}</span>
            </button>
            {moreOpen ? <MorePanel sections={overflow} t={t} /> : null}
          </div>
        </div>
      </div>
    </nav>
  );
}

function MorePanel({
  sections,
  t,
}: {
  sections: SpatialNavSection[];
  t: (key: string) => string;
}) {
  const location = useLocation();
  return (
    <div className="spatial-desk-more-panel" role="menu">
      {sections.map((section) => (
        <div key={section.labelKey} className="spatial-desk-more-group">
          <p className="spatial-desk-more-heading">{t(section.labelKey)}</p>
          {section.items.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey);
            const active = item.isActive
              ? item.isActive(location.pathname)
              : location.pathname === item.to;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? item.to === '/'}
                role="menuitem"
                aria-current={active ? 'page' : undefined}
                onPointerEnter={() => prefetchRoute(item.to)}
                className={cn('spatial-desk-more-item', active && 'is-active')}
              >
                <Icon className="size-4 shrink-0" strokeWidth={1.6} />
                <span>{label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </div>
  );
}
